import React from "react";
import BlogImage from "../assets/blognew1.png";
import hero from "../assets/zaffloBG.png"

export default function DigitalMenuIndia() {
  return (
    <div className="bg-white text-gray-800 overflow-hidden">

      {/* HERO */}
      <section className="relative bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-7xl mx-auto px-6 py-28 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <span className="inline-block mb-4 text-sm uppercase tracking-wider text-[#6cbb23]">
              Digital Menu India
            </span>

            <h1 className="text-4xl md:text-5xl font-bold leading-tight">
              Digital Menus for Indian Businesses in a Mobile-First World
            </h1>

            <p className="mt-6 text-lg text-gray-300">
              Indian customers don’t want to wait, call, or ask for menus.
              They expect speed, clarity, and control — right from their phone.
            </p>

            <p className="mt-4 text-gray-300">
              Zafflo helps restaurants, cafés, hotels, and service businesses
              across India move from printed menus to a modern digital menu
              experience powered by QR codes and WhatsApp.
            </p>

            <div className="mt-10 flex gap-4">
              <a
                href="/signup"
                className="rounded-xl bg-[#6cbb23] px-6 py-3 font-semibold text-white hover:opacity-90"
              >
                Create Digital Menu
              </a>
              <a
                href="/demo"
                className="rounded-xl border border-white/30 px-6 py-3 font-semibold text-white hover:bg-white/10"
              >
                View Live Example
              </a>
            </div>

            <p className="mt-4 text-sm text-gray-400">
              Built for Indian businesses · No app required · WhatsApp-first
            </p>
          </div>

          <div className="hidden lg:block">
            <img
              src={BlogImage}
              alt="Digital Menu India"
              className="w-full max-w-md mx-auto drop-shadow-2xl"
            />
          </div>
        </div>
      </section>

      {/* CONTEXT SECTION */}
      <section className="py-28 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-gray-900">
            Why digital menus are becoming the standard in India
          </h2>

          <p className="mt-6 text-lg text-gray-600">
            From roadside cafés to premium hotels, businesses across India
            are rapidly replacing printed menus with digital alternatives.
          </p>

          <div className="mt-14 rounded-3xl bg-white p-12 shadow-lg text-left">
            <ul className="space-y-6">
              {[
                "Smartphone usage is universal across age groups in India",
                "Customers prefer scanning over touching printed menus",
                "Prices, availability, and GST change frequently",
                "WhatsApp has become the default communication channel",
                "Businesses want faster service without hiring more staff",
              ].map((item, idx) => (
                <li key={idx} className="flex gap-4">
                  <span className="mt-2 h-2 w-2 rounded-full bg-[#6cbb23]" />
                  <p className="text-gray-700">{item}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* WHAT IS A DIGITAL MENU */}
      <section className="py-28 bg-white">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-16 items-center">
          <div>
            <h2 className="text-3xl font-bold">
              What is a digital menu?
            </h2>

            <p className="mt-6 text-gray-600">
              A digital menu is an online version of your menu that customers
              can access instantly by scanning a QR code using their phone.
            </p>

            <p className="mt-4 text-gray-600">
              Unlike printed menus, digital menus are always up to date,
              visually rich, and interactive. Customers can browse categories,
              view images, and place orders or requests without waiting for staff.
            </p>

            <p className="mt-4 text-gray-600">
              With Zafflo, a digital menu is not just a display — it becomes
              a direct ordering and communication channel through WhatsApp.
            </p>
          </div>

          <div className="rounded-2xl h-[320px] flex items-center justify-center text-gray-400">
             <img
              src={hero}
              alt="Digital Menu India"
              className="w-[60%] max-w-md mx-auto drop-shadow-2xl"
            />
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="py-28 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center">
            Business benefits of switching to a digital menu
          </h2>

          <div className="mt-20 grid md:grid-cols-3 gap-12">
            {[
              {
                title: "Faster ordering, higher table turnover",
                desc: "Customers browse and decide at their own pace, reducing wait time and speeding up order placement.",
              },
              {
                title: "Lower operational costs",
                desc: "No repeated printing costs, no outdated menus, and less dependency on staff for basic explanations.",
              },
              {
                title: "Higher average order value",
                desc: "Images, categories, and recommendations encourage customers to explore more items.",
              },
              {
                title: "Consistent experience across locations",
                desc: "Whether you operate one outlet or many, every customer sees the same menu and pricing.",
              },
              {
                title: "Real-time updates",
                desc: "Hide unavailable items, change prices, or add offers instantly without disruption.",
              },
              {
                title: "Customer data & repeat business",
                desc: "Digital menus allow businesses to capture WhatsApp numbers and build long-term relationships.",
              },
            ].map((item, idx) => (
              <div key={idx} className="rounded-2xl bg-white/5 p-8">
                <h3 className="font-semibold text-lg">
                  {item.title}
                </h3>
                <p className="mt-3 text-gray-300">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY ZAFFLO */}
      <section className="py-28 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center">
            Why Indian businesses choose Zafflo
          </h2>

          <p className="mt-6 text-center text-gray-600 max-w-3xl mx-auto">
            There are many digital menu tools available, but Zafflo is built
            specifically for Indian business realities.
          </p>

          <div className="mt-20 grid md:grid-cols-2 gap-12">
            {[
              {
                title: "WhatsApp-first by design",
                desc: "Orders and requests flow directly to WhatsApp — no complex dashboards or training required.",
              },
              {
                title: "Works without apps",
                desc: "Customers don’t need to download anything. A simple scan works on all phones.",
              },
              {
                title: "Built for Indian pricing & GST",
                desc: "Supports Indian price formats, taxes, and frequent menu changes.",
              },
              {
                title: "Affordable for small & growing businesses",
                desc: "Transparent pricing that makes sense for Indian margins.",
              },
            ].map((item, idx) => (
              <div key={idx}>
                <h3 className="font-semibold text-lg">
                  {item.title}
                </h3>
                <p className="mt-3 text-gray-600">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Section */}
<section className="py-28 bg-white">
  <div className="max-w-6xl mx-auto px-6">
    <h2 className="text-3xl font-bold text-gray-900 text-center">
      How Zafflo compares to typical digital menu platforms
    </h2>

    <p className="mt-6 text-lg text-gray-600 text-center max-w-3xl mx-auto">
      Many businesses try digital menus before finding the right fit.
      The differences usually become clear only after day-to-day usage.
    </p>

    <div className="mt-20 grid md:grid-cols-2 gap-12">
      
      {/* Generic Platforms */}
      <div className="rounded-3xl border p-10 bg-gray-50">
        <h3 className="text-xl font-semibold text-gray-800">
          Typical digital menu platforms
        </h3>

        <ul className="mt-6 space-y-5">
          {[
            "Focus mainly on displaying a static menu",
            "Require separate dashboards or apps for staff",
            "Limited flexibility for real-world workflows",
            "Often depend on staff training to operate",
            "Ordering experience feels disconnected",
            "Minimal support for WhatsApp-based communication",
            "Designed for global markets, not Indian realities",
          ].map((item, idx) => (
            <li key={idx} className="flex gap-3">
              <span className="mt-2 h-2 w-2 rounded-full bg-gray-400" />
              <p className="text-gray-700">{item}</p>
            </li>
          ))}
        </ul>
      </div>

      {/* Zafflo */}
      <div className="rounded-3xl border-2 border-[#6cbb23] p-10 bg-white shadow-lg">
        <h3 className="text-xl font-semibold text-gray-900">
          Zafflo’s approach
        </h3>

        <ul className="mt-6 space-y-5">
          {[
            "Built for ordering, not just viewing menus",
            "Works directly with WhatsApp — no extra systems",
            "Fits naturally into existing staff workflows",
            "Minimal training required to go live",
            "Seamless experience across dine-in, delivery, and rooms",
            "Encourages upsells and repeat interactions",
            "Designed specifically for Indian businesses",
          ].map((item, idx) => (
            <li key={idx} className="flex gap-3">
              <span className="mt-2 h-2 w-2 rounded-full bg-[#6cbb23]" />
              <p className="text-gray-800 font-medium">{item}</p>
            </li>
          ))}
        </ul>
      </div>
    </div>

    {/* Summary Statement */}
    <div className="mt-16 max-w-3xl mx-auto text-center">
      <p className="text-lg text-gray-600">
        Zafflo is not built to replace menus —
        <span className="font-semibold text-gray-900">
          {" "}it’s built to simplify operations, improve customer experience,
          and help businesses grow.
        </span>
      </p>
    </div>
  </div>
</section>


      {/* CTA */}
      <section className="py-28 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold">
            Build your digital menu in minutes
          </h2>

          <p className="mt-4 text-gray-600">
            Join Indian businesses using Zafflo to modernize menus, simplify
            ordering, and grow revenue.
          </p>

          <a
            href="/signup"
            className="mt-8 inline-block rounded-xl bg-[#6cbb23] px-8 py-4 text-white font-semibold"
          >
            Get Started with Zafflo
          </a>
        </div>
      </section>
    </div>
  );
}
